import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { IonNav, ModalController } from '@ionic/angular';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MedicalInquiryService, MI_TAB } from '../../../services/medical-inquiry/medical-inquiry.service';
import { MedicalInquirySubmitFlowService } from '../../../services/medical-inquiry/medical-inquiry-submit-flow.service';
import { MedicalInquiryDataService } from '@omni/data-services/medical-inquiry/medical-inquiry.data.service';
import { MedicalInquiry, MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { ActivityService } from '@omni/services/activity/activity.service';
import { EventsService } from '@omni/services/events/events.service';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { DynamicForm, FormType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { UIService } from '@omni/services/ui/ui.service';
import { DeviceService } from '@omni/services/device/device.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { IndPageTitleViewDataModel } from '@omni/models/indPageTitleDataModel';
import { DynamicFormComponent } from '@omni/components/shared/ind-dynamic-form/ind-dynamic-form';
import { DynamicFormComponentModel, DynamicFormType } from '@omni/models/dynamic-form-component.model';
import { MedicalInquiryDualSignatureModalComponent } from '../medical-inquiry-dual-signature-modal/medical-inquiry-dual-signature-modal.component';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FooterService, FooterViews } from '@omni/services/footer/footer.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';

@Component({
  selector: 'medical-inquiry-detail',
  templateUrl: './medical-inquiry-detail.component.html',
  styleUrls: ['./medical-inquiry-detail.component.scss'],
  // OnPush NOT used here — hosts ind-display-form which mutates inputs internally
})
export class MedicalInquiryDetailComponent implements OnInit, OnDestroy {
  @Input() from: string;
  @Input() modalNavCtrl: IonNav;
  /** When true (e.g. agenda clone), open the edit dynamic form once after display data loads. */
  @Input() isCloneFlow = false;
  /** Optional explicit MI (e.g. from ion-nav root params); keeps selection aligned with agenda clone. */
  @Input() activity: MedicalInquiry | null = null;

  formMetadata: DynamicForm;
  rawDataForDisplay: any;
  pageTitle: IndPageTitleViewDataModel;
  /** True while loading display data; used to show loading state instead of "No data" (Option 2). */
  loadingDisplay = false;
  readonly medicalInquiryLinkedEntityPrefix = DB_KEY_PREFIXES.MEDICAL_INQUIRY_LINKED_ENTITY;
  private destroy$ = new Subject<void>();
  private linkEntitiesReqPayload: any[] = [];
  private cloneFlowAutoEditDone = false;
  private _indskrIdRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private _loadDisplayFormInFlight = false;

  get isInMeetingModal(): boolean {
    return this.from === 'InMeetingModal';
  }

  constructor(
    public medicalInquiryService: MedicalInquiryService,
    private readonly medicalInquiryDataService: MedicalInquiryDataService,
    private readonly dynamicFormsService: DynamicFormsService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly uiService: UIService,
    private readonly device: DeviceService,
    private readonly navService: NavigationService,
    private readonly modalCtrl: ModalController,
    private readonly authenticationOfflineService: AuthenticationService,
    private readonly medicalInquirySubmitFlowService: MedicalInquirySubmitFlowService,
    private readonly alertService: AlertService,
    private readonly cdr: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
    private readonly dateTimeFormats: DateTimeFormatsService,
    private readonly activityService: ActivityService,
    private readonly events: EventsService,
    private readonly footerService: FooterService,
  ) {}

  ngOnInit() {
    if (this.activity) {
      this.medicalInquiryService.setSelectedMI(this.activity);
    }
    this.medicalInquiryService.selectedMI$
      .pipe(
        distinctUntilChanged((a, b) => (a?.id ?? null) === (b?.id ?? null)),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        const openedCloneEdit = await this._loadDisplayForm();
        // If user switched to another list item while edit form was open, pop edit so detail (new record) shows.
        // Do not pop when we just auto-opened edit for agenda clone — that would undo clone UX.
        if (
          !openedCloneEdit &&
          this.navService.getActiveChildNavViewPageName() === PageName.DynamicFormComponent
        ) {
          await this.navService.popChildNavPageWithPageTracking();
        }
      });
  }

  ionViewWillEnter() {
    this._initFooterForPresentation();
    if (this.medicalInquiryService.selectedMI?.id && !this._loadDisplayFormInFlight) {
      void this._loadDisplayForm();
    }
  }

  /**
   * In presentation the detail shares the screen with the presentation's own footer, which would
   * otherwise keep Play and the carousel controls live behind an open activity.
   */
  private _initFooterForPresentation(): void {
    if (this.from === 'InMeetingPresentation') {
      this.footerService.initButtons(FooterViews.PREVIEW_MEETING_ACTIVITY);
    }
  }

  ngOnDestroy() {
    this.medicalInquiryService.agendaCloneOpenEditAfterDisplay = false;
    if (this._indskrIdRetryTimer) { clearTimeout(this._indskrIdRetryTimer); this._indskrIdRetryTimer = null; }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @returns true when agenda clone auto-opened the edit form (caller must not pop DynamicForm).
   */
  private async _loadDisplayForm(): Promise<boolean> {
    this._loadDisplayFormInFlight = true;
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id) {
      this._loadDisplayFormInFlight = false;
      this.formMetadata = null;
      this.rawDataForDisplay = null;
      this.pageTitle = null;
      this.cdr.markForCheck();
      return false;
    }

    this._initPageTitle(selected);

    this.formMetadata = this.dynamicFormsService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.DISPLAYFORM);
    if (!this.formMetadata) {
      this.notificationService.notify(
        this.translate.instant('ONE_KEY_DISPLAY_FORM_NO_FORMS_ERR'),
        'MedicalInquiryDetailComponent',
        'top',
        ToastStyle.DANGER,
        3000
      );
      this._loadDisplayFormInFlight = false;
      this.rawDataForDisplay = null;
      this.pageTitle = null;
      this.cdr.markForCheck();
      return false;
    }

    // When edit form is on top (user switched list item), load in background without loader to avoid flicker before pop.
    const editFormIsOnTop = this.navService.getActiveChildNavViewPageName() === PageName.DynamicFormComponent;
    const hasOptimisticData = selected.rawData && typeof selected.rawData === 'object' && !Array.isArray(selected.rawData);
    const showLoader = !editFormIsOnTop && !hasOptimisticData;

    this.rawDataForDisplay = null;
    this.loadingDisplay = true;

    let resolvedDisplay: typeof this.rawDataForDisplay = null;
    const isTeamRecord = this.medicalInquiryService.selectedTab === MI_TAB.TEAM;

    try {
      if (hasOptimisticData) {
        // Option 1: show content immediately from list item; load products in background.
        let base = { ...(selected.rawData as Record<string, unknown>) };
        if (this.device.isOffline) {
          base = await this.medicalInquiryDataService.enrichMedicalInquiryRawDataForDisplay(base) as Record<string, unknown>;
        }
        if (!base['indskr_medicalinquiryid']) (base as any)['indskr_medicalinquiryid'] = selected.id;
        this.rawDataForDisplay = { ...base };
        // Offline: auto-generate plugin never runs, so show a friendly placeholder in the title field.
        if (this.device.isOffline && !this.rawDataForDisplay['indskr_title']) {
          this.rawDataForDisplay['indskr_title'] = this.translate.instant('MEDICAL_INQUIRY');
        }
        this.loadingDisplay = false;
        this.cdr.markForCheck();
      } else if (showLoader) {
        this.uiService.displayLoader();
      }

      // Always force-refresh when online so the display form picks up all Dynamics fields
      // (all-attributes) that the delta sync template does not include.
      const mi = isTeamRecord ? null : await this.medicalInquiryDataService.getMedicalInquiryById(selected.id, !this.device.isOffline);
      let rawData = mi?.rawData ?? selected.rawData ?? null;
      if (this.device.isOffline && rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
        rawData = await this.medicalInquiryDataService.enrichMedicalInquiryRawDataForDisplay(rawData as Record<string, unknown>);
      }
      const base = rawData && typeof rawData === 'object' && !Array.isArray(rawData) ? rawData : {};
      if (!base['indskr_medicalinquiryid']) (base as any)['indskr_medicalinquiryid'] = selected.id;
      resolvedDisplay = rawData ? { ...base } : null;
      // Offline: auto-generate plugin never runs, so show a friendly placeholder in the title field.
      if (resolvedDisplay && this.device.isOffline && !resolvedDisplay['indskr_title']) {
        resolvedDisplay['indskr_title'] = this.translate.instant('MEDICAL_INQUIRY');
      }
      this.rawDataForDisplay = resolvedDisplay;
      // Prefer the force-refreshed MI for the title so _patchTitleFromProduct's result is visible
      // immediately, even on first open when the in-memory list item hasn't been patched yet.
      const titleSource = mi ?? selected;
      if (mi && mi.title !== selected.title) {
        this.medicalInquiryService.selectedMI = mi;
      }
      this._initPageTitle(titleSource);
    } catch {
      resolvedDisplay = selected.rawData ?? null;
      this.rawDataForDisplay = resolvedDisplay;
      this._initPageTitle(selected);
    } finally {
      this._loadDisplayFormInFlight = false;
      this.loadingDisplay = false;
      if (showLoader) this.uiService.dismissLoader();
      this.cdr.detectChanges();
    }

    // Schedule a background retry when indskr_id is still missing after the initial fetch.
    // indskr_id may be set by an async server-side workflow that hasn't completed yet.
    if (this._indskrIdRetryTimer) { clearTimeout(this._indskrIdRetryTimer); this._indskrIdRetryTimer = null; }
    if (!this.device.isOffline && !isTeamRecord && resolvedDisplay && !(resolvedDisplay as Record<string, unknown>)['indskr_id']) {
      this._indskrIdRetryTimer = setTimeout(async () => {
        this._indskrIdRetryTimer = null;
        if (this.medicalInquiryService.selectedMI?.id !== selected.id) return;
        const retried = await this.medicalInquiryDataService.getMedicalInquiryById(selected.id, true);
        if (retried?.rawData && (retried.rawData as Record<string, unknown>)['indskr_id']) {
          this.medicalInquiryDataService.upsertMedicalInquiry(retried);
          this.rawDataForDisplay = { ...(retried.rawData as Record<string, unknown>) };
          this._initPageTitle(retried);
          this.cdr.markForCheck();
        }
      }, 5000);
    }

    const wantsAgendaCloneEdit =
      (this.isCloneFlow || this.medicalInquiryService.agendaCloneOpenEditAfterDisplay) &&
      !this.cloneFlowAutoEditDone &&
      selected.isDraft &&
      selected.isActive &&
      resolvedDisplay &&
      this.formMetadata;

    if (wantsAgendaCloneEdit) {
      this.medicalInquiryService.agendaCloneOpenEditAfterDisplay = false;
      this.cloneFlowAutoEditDone = true;
      await this._editMedicalInquiry();
      return true;
    }

    return false;
  }

  /** True if raw row has a non-empty HCP or rep (user) signature value. */
  private _hasAtLeastOneSignature(raw?: unknown): boolean {
    if (!raw || typeof raw !== 'object') return false;
    const record = raw as Record<string, unknown>;
    const hcp = String(record['indskr_hcpsignature'] ?? '').trim();
    const rep = String(record['indskr_repsignature'] ?? '').trim();
    return hcp.length > 0 || rep.length > 0;
  }

  private _initPageTitle(selected: MedicalInquiry): void {
    const currentUserId = this.authenticationOfflineService.user?.systemUserID;
    const isOwner = selected.ownerId === currentUserId;
    // Non-owners (e.g. managers viewing team records) may only read, not edit or scrap.
    const isReadOnlyMode = !selected.isActive || selected.isSubmitted || !isOwner
      || this.medicalInquiryService.isParentMeetingClosed(selected);
    const isScrapDisabled = isReadOnlyMode;
    const isEditDisabled = isReadOnlyMode;
    
    const showSignatureIcon = this._hasAtLeastOneSignature(this.rawDataForDisplay ?? selected.rawData);
    const buttons: IndPageTitleViewDataModel['controls'] = (selected.isSubmitted || selected.isCompleted)
      ? (
          showSignatureIcon
            ? [{ id: 'signature-captured', imgSrc: 'assets/imgs/signature_captured_online.svg', isDisabled: false, align: 'right' }]
            : []
        )
      : [
          { id: 'scrapmedicalinquiry', imgSrc: 'assets/imgs/header_cancel.svg', name: this.translate.instant('SCRAP'), isDisabled: isScrapDisabled, align: 'right' },
          { id: 'editmedicalinquiry', imgSrc: 'assets/imgs/header_edit.svg', name: this.translate.instant('EDIT'), isDisabled: isEditDisabled, align: 'right' },
        ];
    if (this.isInMeetingModal) {
      buttons.push({ id: 'close', icon: 'chevron-back-outline', isDisabled: false, align: 'left' });
    }
    this.pageTitle = {
      id: 'medical-inquiry-detail-page-title',
      title: selected.displayTitle || this.translate.instant('MEDICAL_INQUIRY'),
      displayDefaultBackButton: !this.isInMeetingModal
        && (this.device.isMobileDevice || this.from === 'AgendaView' || this.from === 'ContactTool'),
      controls: buttons,
    };
  }

  onButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'scrapmedicalinquiry':
        this._scrapMedicalInquiry();
        break;
      case 'editmedicalinquiry':
        this._editMedicalInquiry();
        break;
      case 'signature-captured':
        this._showSignaturesCapturedModal();
        break;
      case 'close':
        this.onBackClick();
        break;
      default:
        break;
    }
  }

  private async _showSignaturesCapturedModal(): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id || !selected.isSubmitted) return;

    const raw = selected.rawData || {};
  
    const hcpSig = (raw['indskr_hcpsignature'] as string) || null;
    const repSig = (raw['indskr_repsignature'] as string) || null;
    if (!hcpSig && !repSig) return;

    const hcpSignatureTerms = (raw['indskr_hcpsignatureterms'] as string) || '';
    const hcpSignatureDate = raw['indskr_hcpsignaturedate']
      ? this._formatDateTimeForDisplay(raw['indskr_hcpsignaturedate'] as string)
      : null;
    const repSignatureDate = raw['indskr_usersignaturedate']
      ? this._formatDateTimeForDisplay(raw['indskr_usersignaturedate'] as string)
      : null;

    const modal = await this.modalCtrl.create({
      component: MedicalInquiryDualSignatureModalComponent,
      componentProps: {
        viewOnly: true,
        hcpSignature: hcpSig,
        repSignature: repSig,
        hcpContactName: selected.contactFormatted || '',
        signatureCapturedBy:
          (selected.rawData?.['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string) || '',
        showHcpSignature: this.showHcpSignature,
        showRepSignature: this.showRepSignature,
        hcpSignatureTerms,
        viewOnlyHcpSignatureDate: hcpSignatureDate,
        viewOnlyRepSignatureDate: repSignatureDate,
      },
      backdropDismiss: true,
      cssClass: 'medical-inquiry-dual-signature-modal',
    });
    await modal.present();
  }

  private _formatDateTimeForDisplay(isoDate: string): string | null {
    if (!isoDate) return null;
    try {
      return this.datePipe.transform(isoDate, this.dateTimeFormats.dateTime, undefined, this.translate.currentLang) || null;
    } catch {
      return null;
    }
  }

  private async _scrapMedicalInquiry(): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id || !selected.isActive) return;

    const alertResult = await this.alertService.showAlert(
      {
        title: this.translate.instant('POP_SCRAP_INQUIRY'),
        message: this.translate.instant('POP_R_U_SURE_SCRAP_SELECTED_INQUIRY'),
      },
      this.translate.instant('SCRAP'),
      this.translate.instant('CANCEL')
    );
    if (alertResult?.role !== 'ok') return;

    await this.uiService.displayLoader();
    try {
      const isOnline = !this.device.isOffline;
      await this.medicalInquiryService.scrapMedicalInquiryAndSync(selected);
      if (!isOnline) {
        this.notificationService.notify(
          this.translate.instant('SAVED_OFFLINE_WILL_SYNC') || 'Saved offline. Will sync when connected.',
          'MedicalInquiryDetailComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
      } else {
        this.notificationService.notify(this.translate.instant('UPDATED_SUCCESS'), 'MedicalInquiryDetailComponent', 'top', ToastStyle.INFO, 3000);
      }

      this.medicalInquiryService.setSelectedMI(null);

      // Navigate back to meeting or close modal
      if (this.isInMeetingModal) {
        this.modalCtrl.dismiss();
      }
    } catch (err) {
      console.error('MedicalInquiryDetailComponent: scrap failed', err);
      this.notificationService.notify(this.translate.instant('FAILED'), 'MedicalInquiryDetailComponent', 'top', ToastStyle.DANGER, 3000);
    } finally {
      this.uiService.dismissLoader();
    }
  }

  private async _editMedicalInquiry(): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id || !selected.isActive) return;

    this.medicalInquiryService.setFormDirty(false);

    const editForm = this.dynamicFormsService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.EDITFORM);
    if (!editForm) return;

    if (this.device.isOffline) {
      const cachedOptions = await this.dynamicFormsService.getMIFormOptionSetsFromDb();
      this.dynamicFormsService.hydrateMedicalInquiryFormOptionSets(editForm, cachedOptions);
    }

    let rawData = selected.rawData || {};
    if (this.device.isOffline) {
      rawData = await this.medicalInquiryDataService.enrichMedicalInquiryRawDataForDisplay(rawData as Record<string, unknown>) as Record<string, unknown>;
    }
    const mainEntityObjectId = { indskr_medicalinquiryid: selected.id };
    const existingProducts = (this.rawDataForDisplay?.globalSearchedDataLinkEntities?.[this.medicalInquiryLinkedEntityPrefix + 'indskr_medicalinquiryproduct'])
      ?? (this.rawDataForDisplay?.globalSearchedDataLinkEntities?.[this.medicalInquiryLinkedEntityPrefix + 'indskr_medicalinquiryproducts'])
      ?? [];

    const editFormEntityObject: Record<string, unknown> = { ...rawData };
    // Offline: pre-populate the title field so it is not visually blank (auto-generate plugin is server-side only).
    // Stripped back to '' before any Dynamics call so auto-generate still fires on sync.
    if (this.device.isOffline && !editFormEntityObject['indskr_title']) {
      editFormEntityObject['indskr_title'] = this.translate.instant('MEDICAL_INQUIRY');
    }

    const formDetails: DynamicFormComponentModel = {
      title: (selected.displayTitle || this.translate.instant('MEDICAL_INQUIRY')),
      LookupEntitySetName: 'indskr_medicalinquiries',
      showLeftHeaderButton: true,
      leftHeaderBtnIcon: 'chevron-back-outline',
      showRightHeaderButton: true,
      rightHeaderImgSrcArray: ['assets/imgs/header_save.svg', 'assets/imgs/submit_white.svg', 'assets/imgs/header_cancel.svg'],
      rightHeaderBtnTextArray: [this.translate.instant('Save'), this.translate.instant('Submit'), this.translate.instant('SCRAP')],
      rightHeaderBtnIdsArray: ['SAVEMEDICALINQUIRY', 'SUBMITMEDICALINQUIRY', 'SCRAPMEDICALINQUIRY'],
      formMetadata: editForm,
      formEntityObject: editFormEntityObject,
      mainEntityObjectId,
      dynamicFormType: DynamicFormType.CONFIGUREDFORM,
      linkedEntityValue: existingProducts,
      navOptions: { animate: false },
      eventsHandler: (data: any, eventTarget: string) => this._handleMedicalInquiryFormEvent(data, eventTarget),
      footerView: FooterViews.MedicalInquiryDetail,
      // See the create form: presentation owns the footer, and this form already carries
      // SUBMITMEDICALINQUIRY in rightHeaderBtnIdsArray.
      hideFooterToolbar: this.from === 'InMeetingPresentation',
      loadAddressFromLocal: true,
      modalNavCtrl: this.isInMeetingModal ? this.modalNavCtrl : undefined,
    };

    const navParams = {
      MODE: 'EDIT_MODE',
      FORM_DETAIL: formDetails,
      LINKED_ENTITY_DB_PREFIX: this.medicalInquiryLinkedEntityPrefix,
      RAW_DATA: this.rawDataForDisplay,
      REF_ENTITY_NAME: 'indskr_medicalinquiryid',
    };

    if (this.isInMeetingModal && this.modalNavCtrl) {
      setTimeout(() => this.modalNavCtrl.push(DynamicFormComponent, navParams));
    } else {
      let masterPage = PageName.MedicalInquiryHomePage;
      if (this.from === 'AgendaView') {
        masterPage = PageName.GoAppActivitiesPageComponent;
      } else if (this.from === 'InMeetingPresentation') {
        masterPage = PageName.PresentationMeetingComponent;
      } else if (this.from === 'ContactTool') {
        masterPage = PageName.ContactPageComponent;
      }
      this.navService.pushChildNavPageWithPageTracking(
        PageName.DynamicFormComponent,
        PageName.DynamicFormComponent,
        masterPage,
        navParams
      );
    }
  }

  private async _handleMedicalInquiryFormEvent(data: any, eventTarget: string): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id) return;

    switch (eventTarget) {
      case 'SAVEMEDICALINQUIRY':
        await this._handleEditSave(data, false);
        break;
      case 'SUBMITMEDICALINQUIRY':
        await this._handleSubmitMedicalInquiry(data);
        break;
      case 'SCRAPMEDICALINQUIRY':
        await this._scrapMedicalInquiry();
        break;
      case 'CANCELBUTTONCLICK': {
        const isDirtyAtCancel = data?.displayConfirmation ?? data?.isDirty ?? false;
        this.medicalInquiryService.setFormDirty(!!isDirtyAtCancel);
        if (isDirtyAtCancel && !(await this.medicalInquiryService.confirmDiscardIfDirty())) {
          return;
        }
        this.medicalInquiryService.setFormDirty(false);
        if (this.isInMeetingModal && this.modalNavCtrl) {
          this.modalNavCtrl.pop();
        } else {
          this.navService.popChildNavPageWithPageTracking();
        }
        break;
      }
      case 'ISFORMDIRTY':
        this.medicalInquiryService.setFormDirty(data?.isDirty ?? false);
        break;
      case 'ADDEDLINKEDENTITY':
      case 'SCRAPLINKEDENTITY':
        if (data?.entity && data?.values) MedicalInquiryService.upsertLinkEntityPayload(this.linkEntitiesReqPayload, data);
        break;
      default:
        break;
    }
  }

  private async _flushPendingLinkedEntitiesOffline(
    miId: string,
    eventsHandler?: (data: any, eventName: string) => void
  ): Promise<void> {
    if (!this.linkEntitiesReqPayload?.length) return;
    const addPayloads = this.linkEntitiesReqPayload.filter(p => !(p.values?.statecode === 1));
    for (const p of addPayloads) {
      if (p.linkedEntityId || (p.values as any)?.guid) continue;
      const tempId = await this.medicalInquiryDataService.addProductOffline(miId, p.values, p.formStringValue);
      if (eventsHandler && tempId && p.addedNewDataId) {
        eventsHandler({ addedNewDataId: p.addedNewDataId, linkedEntityId: tempId }, 'LINKEDENTITYIDUPDATE');
      }
    }
    const deletePayloads = this.linkEntitiesReqPayload.filter(
      p => p.values?.statecode === 1 && p.linkedEntityId
    );
    for (const p of deletePayloads) {
      await this.medicalInquiryDataService.markProductDeletedOffline(miId, p.linkedEntityId);
    }
    this.linkEntitiesReqPayload = [];
  }

  private async _handleSubmitMedicalInquiry(data: any): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    const signatureResult = await this.medicalInquirySubmitFlowService.requestSubmitWithSignatures({
      hcpContactName: selected?.contactFormatted || '',
    });
    if (!signatureResult) return;

    if (this.device.isOffline) {
      await this.uiService.displayLoader();
      try {
        await this._flushPendingLinkedEntitiesOffline(selected.id, data?.eventsHandler);
        const formPayload = data?.formValue || data;
        if (formPayload && typeof formPayload === 'object' && Object.keys(formPayload).length > 0) {
          this._stripTitlePlaceholder(formPayload as Record<string, unknown>);
          this.medicalInquiryDataService.updateMedicalInquiryOffline(selected.id, formPayload);
        }
        this.medicalInquiryDataService.submitMedicalInquiryOffline(
          selected.id,
          signatureResult.hcpSignature,
          signatureResult.repSignature,
          signatureResult.hcpSignatureDateRaw,
          signatureResult.repSignatureDateRaw,
          signatureResult.hcpSignatureTerms
        );
        const updated = this.medicalInquiryDataService.medicalInquiries.find(m => m.id === selected.id);
        if (updated) {
          this.medicalInquiryService.setSelectedMI(updated);
          this.medicalInquiryService.syncMedicalInquiryToAgenda(updated);
        }
        this.notificationService.notify(
          this.translate.instant('SUBMITTED_OFFLINE_WILL_SYNC') || 'Submitted offline. Will sync when connected.',
          'MedicalInquiryDetailComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
        this.medicalInquiryService.setFormDirty(false);
        if (this.isInMeetingModal && this.modalNavCtrl) {
          this.modalNavCtrl.pop();
        } else {
          this.navService.popChildNavPageWithPageTracking();
        }
        this._loadDisplayForm();
      } finally {
        this.uiService.dismissLoader();
      }
      return;
    }

    const formPayload = data?.formValue || data || {};
    // Strip placeholder before spreading so it is never included in the submit payload.
    if (typeof formPayload === 'object' && formPayload !== null) {
      this._stripTitlePlaceholder(formPayload as Record<string, unknown>);
    }
    const submitPayload = typeof formPayload === 'object' && formPayload !== null
      ? {
          ...formPayload,
          statuscode: MedicalInquiryStatus.SUBMITTED,
          indskr_hcpsignature: signatureResult.hcpSignature,
          indskr_repsignature: signatureResult.repSignature,
          ...(signatureResult.hcpSignatureDateRaw ? { indskr_hcpsignaturedate: signatureResult.hcpSignatureDateRaw } : {}),
          ...(signatureResult.repSignatureDateRaw ? { indskr_usersignaturedate: signatureResult.repSignatureDateRaw } : {}),
          ...(signatureResult.hcpSignatureTerms ? { indskr_hcpsignatureterms: signatureResult.hcpSignatureTerms } : {}),
        }
      : {
          statuscode: MedicalInquiryStatus.SUBMITTED,
          indskr_hcpsignature: signatureResult.hcpSignature,
          indskr_repsignature: signatureResult.repSignature,
          ...(signatureResult.hcpSignatureDateRaw ? { indskr_hcpsignaturedate: signatureResult.hcpSignatureDateRaw } : {}),
          ...(signatureResult.repSignatureDateRaw ? { indskr_usersignaturedate: signatureResult.repSignatureDateRaw } : {}),
          ...(signatureResult.hcpSignatureTerms ? { indskr_hcpsignatureterms: signatureResult.hcpSignatureTerms } : {}),
        };
    await this._handleEditSave({ ...data, formValue: submitPayload }, true);
  }

  /**
   * Strip the "Medical Inquiry" placeholder from a save payload before it reaches Dynamics.
   * Sending '' (empty string) lets the auto-generate plugin produce a real title.
   * A value the user typed themselves will differ from the placeholder and is left untouched.
   */
  private _stripTitlePlaceholder(payload: Record<string, unknown>): void {
    if (payload['indskr_title'] === this.translate.instant('MEDICAL_INQUIRY')) {
      payload['indskr_title'] = '';
    }
  }

  private async _handleEditSave(data: any, popAfterSave: boolean): Promise<void> {
    const selected = this.medicalInquiryService.selectedMI;
    if (!selected?.id) return;

    const payload = data?.formValue || data;
    if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) return;
    this._stripTitlePlaceholder(payload);

    await this.uiService.displayLoader();
    try {
      if (this.device.isOffline) {
        await this._flushPendingLinkedEntitiesOffline(selected.id, data?.eventsHandler);
        this.medicalInquiryDataService.updateMedicalInquiryOffline(selected.id, payload);
        const updated = this.medicalInquiryDataService.medicalInquiries.find(m => m.id === selected.id);
        if (updated) {
          this.medicalInquiryService.setSelectedMI(updated);
          this.medicalInquiryService.syncMedicalInquiryToAgenda(updated);
        }
        this.notificationService.notify(
          this.translate.instant('SAVED_OFFLINE') || 'Saved offline.',
          'MedicalInquiryDetailComponent',
          'top',
          ToastStyle.INFO,
          3000
        );
        this.medicalInquiryService.setFormDirty(false);
        if (data?.eventsHandler) {
          data.eventsHandler({ indskr_medicalinquiryid: selected.id, indskr_id: (updated?.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' }, 'NEWDATAUPDATED');
        }
        if (popAfterSave) {
          if (this.isInMeetingModal && this.modalNavCtrl) {
            this.modalNavCtrl.pop();
          } else {
            this.navService.popChildNavPageWithPageTracking();
          }
          this._loadDisplayForm();
        }
        return;
      }

      const { miResult, addedProducts } = await this.medicalInquiryDataService.saveMedicalInquiryWithProducts(
        selected.id,
        this.linkEntitiesReqPayload ?? [],
        payload
      );
      this.linkEntitiesReqPayload = [];
      addedProducts
        .filter(({ change }) => change.addedNewDataId)
        .forEach(({ change, guid }) => data?.eventsHandler?.({ addedNewDataId: change.addedNewDataId, linkedEntityId: guid }, 'LINKEDENTITYIDUPDATE'));
      if (miResult instanceof Error) throw miResult;
      const response = miResult;
      let updated = response ? this.medicalInquiryDataService.upsertMedicalInquiryFromApiResponse(response) : null;
      // Always force-refresh from server after save to pick up server-generated fields
      // (indskr_id auto-number, indskr_title with product name from post-operation plugins).
      if (!this.device.isOffline) {
        const refreshed = await this.medicalInquiryDataService.getMedicalInquiryById(selected.id, true);
        if (refreshed) {
          this.medicalInquiryDataService.upsertMedicalInquiry(refreshed);
          updated = refreshed;
        }
      }
      if (updated) {
        this.medicalInquiryService.setSelectedMI(updated);
        this.medicalInquiryService.syncMedicalInquiryToAgenda(updated);
        // selectedMI$ uses distinctUntilChanged by ID, so it won't re-fire for an in-place update.
        // Update rawDataForDisplay and page title directly so the view reflects server-generated fields.
        if (!popAfterSave) {
          this.rawDataForDisplay = { ...(updated.rawData as Record<string, unknown>) };
          this._initPageTitle(updated);
          this.cdr.markForCheck();
        }
      }

      this.notificationService.notify(
        this.translate.instant(popAfterSave ? 'MEDICAL_INQUIRY_SUBMITTED_SUCCESS' : 'MEDICAL_INQUIRY_SAVED_SUCCESS'),
        'MedicalInquiryDetailComponent',
        'top',
        ToastStyle.INFO,
        3000
      );
      this.medicalInquiryService.setFormDirty(false);
      if (data?.eventsHandler) {
        data.eventsHandler(
          { indskr_medicalinquiryid: selected.id, indskr_title: updated?.title ?? (response as Record<string, unknown>)?.['indskr_title'] ?? '', indskr_id: (updated?.rawData as Record<string, unknown>)?.['indskr_id'] as string ?? '' },
          'NEWDATAUPDATED'
        );
      }
      if (popAfterSave) {
        if (this.isInMeetingModal && this.modalNavCtrl) {
          this.modalNavCtrl.pop();
        } else {
          this.navService.popChildNavPageWithPageTracking();
        }
        this._loadDisplayForm();
      }
    } catch (err) {
      console.error('MedicalInquiryDetailComponent: edit save failed', err);
      this.notificationService.notify(this.translate.instant('FAILED'), 'MedicalInquiryDetailComponent', 'top', ToastStyle.DANGER, 3000);
      if (data?.eventsHandler) {
        data.eventsHandler('ERROR', 'NEWDATAUPDATED');
      }
    } finally {
      this.uiService.dismissLoader();
    }
  }

  onBackClick() {
    if (this.isInMeetingModal) {
      this.modalCtrl.dismiss();
      return;
    }
    this.medicalInquiryService.setSelectedMI(null);

    if (this.from === 'AgendaView') {
      this.activityService.selectedActivity = undefined;
      this.activityService.didOpenDetailPageFromAgendaPage = false;
      this.uiService.showRightPane = false;
      this.navService.setChildNavRightPaneView(false);
      this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
      if (this.activityService.activityOpenedFromTodo) {
        this.events.publish('scrollToDate');
      }
    } else if (this.from === 'ContactTool') {
      // The detail sits on top of the contact's own pane, so popping lands back on the contact.
      this.footerService.initButtons(FooterViews.Contacts);
      void this.navService.popChildNavPageWithPageTracking();
    }
  }

  get selectedMI(): MedicalInquiry {
    return this.medicalInquiryService.selectedMI;
  }

  get showHcpSignature(): boolean {
    return Boolean(this.authenticationOfflineService.hasIoConfigWithFeatureCode(
      IoConfigurationFeatureCodes.BU_MEDICALINQUIRY_GA_MANDATORYHCPSIGNATURE
    ));
  }

  get showRepSignature(): boolean {
    return Boolean(this.authenticationOfflineService.hasIoConfigWithFeatureCode(
      IoConfigurationFeatureCodes.BU_MEDICALINQUIRY_GA_USERSIGNATURE
    ));
  }
}
